import { FirebaseApp } from '@firebase/app';
import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  DocumentData,
  DocumentSnapshot,
  FieldPath,
  Firestore,
  getDocFromServer,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryConstraint,
  QueryDocumentSnapshot,
  QuerySnapshot,
  setDoc,
  SnapshotOptions,
  Timestamp,
  Unsubscribe,
  where,
  writeBatch,
} from 'firebase/firestore';
import { fromUnixTime, isDate, isValid, toDate } from 'date-fns';
import { merge, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { Auth, getAuth } from 'firebase/auth';
import * as uuid from 'uuid';
import { BaseModel, BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { ChangeSourceType } from '@ag-common-lib/public-api';
import { classToPlain } from 'class-transformer';

export interface FetchOptions {
  includeRef?: boolean;
  sortField?: string;
  order?: 'asc' | 'desc';
  limit?: number;
  ignoreCash?: boolean;
}

const localeCompareOptions: Intl.CollatorOptions = {
  numeric: true,
  sensitivity: 'base',
  ignorePunctuation: true,
};

/**
 * A Firestore listener error, made loud.
 *
 * These handlers used to swallow the error with a bare `debugger`. That matters most for
 * permission-denied: the listener detaches, the grid renders empty, and nothing anywhere
 * says why — so a screen reading a collection it is no longer allowed to read looks
 * merely empty.
 */
function reportFirestoreListenerError(table: string, error: unknown): void {
  const code = (error as { code?: string })?.code ?? 'unknown';
  const message = (error as { message?: string })?.message ?? String(error);

  if (code === 'permission-denied') {
    console.error(`[firestore] PERMISSION DENIED reading "${table}" — the listener detached and this view will render empty.`, error);
    return;
  }

  console.error(`[firestore] listener error on "${table}" (${code}): ${message}`, error);
}

export class CommonFireStoreDao<T extends BaseModel> {
  readonly db: Firestore;
  readonly auth: Auth;
  private fromFirestore: (documentData: DocumentData) => T = null;
  private toFirestore: (item: T) => DocumentData | T = null;

  private readonly updatesToSkip = new Set();

  constructor(
    fireBaseApp: FirebaseApp,
    fromFirestore: (data: Partial<T>) => T = null,
    toFirestore: (item: T) => DocumentData | T = null,
  ) {
    this.db = getFirestore(fireBaseApp);

    this.auth = getAuth(fireBaseApp);
    this.fromFirestore = fromFirestore ?? null;
    this.toFirestore = toFirestore ?? null;
  }

  async createWithId(value: T, uid: string, table: string): Promise<T> {
    const currentUser = this.auth?.currentUser;
    const idTokenResult = !!currentUser ? await currentUser?.getIdTokenResult() : null;
    const loggedInAgentDbId = idTokenResult?.claims?.agentDbId;
    const actionName = !(currentUser?.uid || currentUser?.email || loggedInAgentDbId)
      ? ChangeSourceType.system
      : value?.[BaseModelKeys.actionName];
    console.log('%cCREATE sourceType set as ', 'color: green', actionName);
    const ref = doc(this.db, table, uid).withConverter({
      fromFirestore: null,
      toFirestore: (item: T): DocumentData => {
        return Object.assign(this.toFirestore ? this.toFirestore(item) : item, {
          [BaseModelKeys.dbId]: uid,
          [BaseModelKeys.actionId]: uuid.v4(),
          [BaseModelKeys.createdDate]: new Date(),
          [BaseModelKeys.createdBy]: currentUser?.uid ?? null,
          [BaseModelKeys.createdByEmail]: currentUser?.email ?? item?.created_by ?? null,
          [BaseModelKeys.createdByAgentDbId]: loggedInAgentDbId ?? null,
          [BaseModelKeys.actionName]: actionName,
        });
      },
    });

    await setDoc(ref, value);

    return this.getById(table, uid);
  }

  async batchCreate(values: T[], table: string): Promise<void> {
    const currentUser = this.auth?.currentUser;
    const idTokenResult = !!currentUser ? await currentUser?.getIdTokenResult() : null;
    const loggedInAgentDbId = idTokenResult?.claims?.agentDbId;
    const collectionRef = collection(this.db, table).withConverter({
      fromFirestore: null,
      toFirestore: (item: T, ...rest): DocumentData => {
        return Object.assign(
          {
            [BaseModelKeys.actionId]: uuid.v4(),
            [BaseModelKeys.createdDate]: new Date(),
            [BaseModelKeys.createdBy]: currentUser?.uid ?? null,
            [BaseModelKeys.createdByEmail]: currentUser?.email ?? item?.created_by ?? null,
            [BaseModelKeys.createdByAgentDbId]: loggedInAgentDbId ?? null,
          },
          this.toFirestore ? this.toFirestore(item) : item,
        );
      },
    });
    const batchSize = 500;
    const batchCount = Math.ceil(values.length / batchSize);

    for (let i = 0; i < batchCount; i++) {
      const startIndex = i * batchSize;
      const endIndex = (i + 1) * batchSize;
      const batchValues = values.slice(startIndex, endIndex);
      const batch = writeBatch(this.db);

      for (let value of batchValues) {
        const docRef = value?.[BaseModelKeys.dbId]
          ? doc(collectionRef, value?.[BaseModelKeys.dbId])
          : doc(collectionRef);

        batch.set(docRef, Object.assign({ [BaseModelKeys.dbId]: docRef.id }, value));
      }

      await batch.commit();
    }
  }

  getDocDbId(table: string): string {
    const ref = collection(this.db, table);
    return doc(ref)?.id;
  }

  async create(value: T, table: string, id?: string): Promise<T> {
    const currentUser = this.auth?.currentUser;
    const idTokenResult = !!currentUser ? await currentUser?.getIdTokenResult() : null;
    const loggedInAgentDbId = idTokenResult?.claims?.agentDbId;
    const actionName = !(currentUser?.uid || currentUser?.email || loggedInAgentDbId)
      ? ChangeSourceType.system
      : value?.[BaseModelKeys.actionName];
    console.log('%cCREATE sourceType set as ', 'color: green', actionName);

    const ref = collection(this.db, table).withConverter({
      fromFirestore: null,
      toFirestore: (item: T, ...rest): DocumentData => {
        return Object.assign(
          {
            [BaseModelKeys.actionId]: uuid.v4(),
            [BaseModelKeys.createdDate]: new Date(),
            [BaseModelKeys.createdBy]: currentUser?.uid ?? null,
            [BaseModelKeys.createdByEmail]: currentUser?.email ?? item?.created_by ?? null,
            [BaseModelKeys.createdByAgentDbId]: loggedInAgentDbId ?? null,
          },
          this.toFirestore ? this.toFirestore(item) : item,
        );
      },
    });

    const docRef = id ? doc(ref, id) : doc(ref);
    const plainObject = classToPlain({
      [BaseModelKeys.dbId]: docRef.id,
      ...value,
    });

    await setDoc(docRef, plainObject, { merge: true });

    return this.getById(table, docRef.id);
  }

  getCollectionGroupSnapshot(
    table,
    queries: QueryParam[] = [],
    updateSnapshot?: boolean,
  ): Observable<QuerySnapshot<T>> {
    return new Observable(observer => {
      const queryConstraints: QueryConstraint[] = queries.map(query => {
        if (query.operation === 'in') {
          // debugger;
        }
        return where(query.field, query.operation, query.value);
      });
      const collectionRef = collectionGroup(this.db, table).withConverter({
        toFirestore: this.normalizeUpdateRequest,
        fromFirestore: this.convertResponse,
      });
      const collectionQuery = query(collectionRef, ...queryConstraints);

      let unsubscribe: Unsubscribe;

      const subscribe = () => {
        unsubscribe = onSnapshot(
          collectionQuery,
          { includeMetadataChanges: true },
          snapshot => {
            observer.next(snapshot);
          },
          error => {
            // Surfaced deliberately. A denied read detaches the listener and the screen
            // simply renders empty, which is indistinguishable from "no data" — the
            // failure mode that hides a missed migration or a rules change.
            reportFirestoreListenerError(table, error);
          },
        );
      };

      this.auth.beforeAuthStateChanged(user => {
        if (!user && unsubscribe) {
          unsubscribe();
        }
      });

      this.auth.onAuthStateChanged(user => {
        if (user) {
          subscribe();
        }
      });
    });
  }

  getCollectionSnapshot(
    table,
    queries: QueryParam[] = [],
    fetchOptions?: FetchOptions,
    requireAuth: boolean = true,
  ): Observable<QuerySnapshot<T[]>> {
    const ignoreCash = fetchOptions?.ignoreCash ?? false;

    return new Observable(observer => {
      const queryConstraints: QueryConstraint[] = queries.map(query => {
        if (query.operation === 'in') {
          // debugger;
        }
        return where(query.field, query.operation, query.value);
      });

      if (fetchOptions && fetchOptions?.limit) {
        queryConstraints.push(orderBy(fetchOptions.sortField, fetchOptions.order ?? 'desc'), limit(fetchOptions.limit));
      }

      const collectionRef = collection(this.db, table).withConverter({
        toFirestore: this.normalizeUpdateRequest,
        fromFirestore: this.convertResponse,
      });
      const collectionQuery = query(collectionRef, ...queryConstraints);

      /** Cleanup fn for the active Firestore snapshot listener. */
      let unsubscribe: Unsubscribe | undefined;
      /** Cleanup fn for the auth-state subscription that starts/restarts the snapshot listener. */
      let authUnsubscribe: Unsubscribe | undefined;
      /** Cleanup fn for the pre-auth-change hook that tears down the snapshot before logout completes. */
      let beforeAuthUnsubscribe: Unsubscribe | undefined;

      const subscribe = () => {
        unsubscribe?.();
        unsubscribe = onSnapshot(
          collectionQuery,
          { includeMetadataChanges: true },
          snapshot => {
            if (ignoreCash && snapshot?.metadata?.fromCache) {
              return;
            }
            const changes = snapshot.docChanges({ includeMetadataChanges: true });

            if (!this.updatesToSkip.size) {
              observer.next(snapshot);
              return;
            }

            const skip =
              !changes?.length ||
              changes.every(docChange => {
                return this.updatesToSkip.has(docChange.doc.id);
              });

            if (!skip) {
              observer.next(snapshot);
              return;
            }

            changes.forEach(docChange => {
              return this.updatesToSkip.delete(docChange.doc.id);
            });
          },
          error => {
            // Surfaced deliberately. A denied read detaches the listener and the screen
            // simply renders empty, which is indistinguishable from "no data" — the
            // failure mode that hides a missed migration or a rules change.
            reportFirestoreListenerError(table, error);
          },
        );
      };

      if (!requireAuth) {
        subscribe();
      }

      beforeAuthUnsubscribe = this.auth.beforeAuthStateChanged(user => {
        if (!user && unsubscribe) {
          unsubscribe();
        }
      });

      authUnsubscribe = this.auth.onAuthStateChanged(user => {
        if (user) {
          subscribe();
        }
      });

      return () => {
        unsubscribe?.();
        authUnsubscribe?.();
        beforeAuthUnsubscribe?.();
      };
    });
  }

  trackCollectionChanges(table, queries: QueryParam[] = []) {
    return merge(
      this.getCollectionSnapshot(table, queries, {
        sortField: BaseModelKeys.createdDate,
        order: 'desc',
        limit: 1,
      }),
      this.getCollectionSnapshot(table, queries, {
        sortField: BaseModelKeys.updatedDate,
        order: 'desc',
        limit: 1,
      }),
    );
  }

  getList(table, queries: QueryParam[] = [], fetchOptions?: FetchOptions, requireAuth?: boolean): Observable<T[]> {
    return this.getCollectionSnapshot(table, queries, fetchOptions, requireAuth).pipe(
      map((collectionSnapshot: any) => {
        const items = collectionSnapshot.docs.map(document => {
          if (!document.exists()) {
            return null;
          }

          const data = document.data();
          if (fetchOptions?.includeRef ?? false) {
            Object.assign(data, { [BaseModelKeys.firebaseRef]: document.ref });
          }

          return data;
        });

        if (fetchOptions?.sortField) {
          items.sort((left, right) =>
            String(left[fetchOptions?.sortField]).localeCompare(
              String(right[fetchOptions?.sortField]),
              'en',
              localeCompareOptions,
            ),
          );
        }

        return items;
      }),
    );
  }

  async getAll(table: string, sortField?: string): Promise<T[]> {
    const collectionRef = collection(this.db, table).withConverter({
      toFirestore: this.normalizeUpdateRequest,
      fromFirestore: this.convertResponse,
    });

    const querySnapshot = await getDocs(collectionRef);

    const docsData = querySnapshot.docs.map(item => (item.exists() ? item.data() : null));

    if (sortField) {
      docsData.sort((left, right) =>
        String(left[sortField]).localeCompare(String(right[sortField]), 'en', localeCompareOptions),
      );
    }

    return docsData;
  }

  async getMostRecentOrderBy(table: string, order: string): Promise<T[]> {
    const ref = collection(this.db, table).withConverter({
      toFirestore: this.normalizeUpdateRequest,
      fromFirestore: this.convertResponse,
    });

    const q = query(ref, orderBy(order, 'desc'), limit(1));
    const snap = await getDocs(q);
    const docsData = snap.docs.map(item => (item.exists() ? item.data() : null));

    if (order) {
      docsData.sort((left, right) =>
        String(left[order]).localeCompare(String(right[order]), 'en', localeCompareOptions),
      );
    }

    return docsData;
  }

  getDocReference(table: string, id: string) {
    if (!id) {
      return null;
    }

    return doc(this.db, table, id).withConverter({
      toFirestore: this.normalizeUpdateRequest,
      fromFirestore: this.convertResponse,
    });
  }

  getDocument(table: string, id: string): Observable<DocumentSnapshot<T>> {
    return new Observable(observer => {
      const ref = this.getDocReference(table, id);

      let unsubscribe: Unsubscribe;

      const subscribe = () => {
        unsubscribe = onSnapshot(
          ref,
          { includeMetadataChanges: true },
          snapshot => {
            // if (!snapshot.metadata.fromCache) {
            //   observer.next(snapshot);
            // }
            observer.next(snapshot);
          },
          error => {
            reportFirestoreListenerError(table, error);
          },
        );
      };

      this.auth.beforeAuthStateChanged(user => {
        if (!user && unsubscribe) {
          unsubscribe();
        }
      });

      this.auth.onAuthStateChanged(user => {
        if (user) {
          subscribe();
        }
      });
    }).pipe(shareReplay<DocumentSnapshot<T>>(1));
  }

  async getById(table: string, id: string): Promise<T> {
    const ref = this.getDocReference(table, id);

    if (!ref) {
      return null;
    }

    const snap = await getDocFromServer(ref);
    const isExist = snap?.exists();

    return isExist ? (snap?.data() as T) : null;
  }

  async delete(id: string, table: string, action: ChangeSourceType | null = null): Promise<void> {
    const ref = doc(this.db, table, id);
    const currentUser = this.auth?.currentUser;
    const idTokenResult = await this.auth.currentUser.getIdTokenResult();
    const loggedInAgentDbId = idTokenResult?.claims?.agentDbId;
    const actionName = !(currentUser?.uid || currentUser?.email || loggedInAgentDbId)
      ? ChangeSourceType.system
      : action;
    console.log('%cDELETE sourceType set as ', 'color: green', actionName);

    await setDoc(
      ref,
      {
        [BaseModelKeys.actionId]: uuid.v4(),
        [BaseModelKeys.actionName]: actionName,
        [BaseModelKeys.deletedDate]: new Date(),
        [BaseModelKeys.deletedBy]: currentUser?.uid ?? null,
        [BaseModelKeys.deletedByEmail]: currentUser?.email ?? null,
        [BaseModelKeys.deletedByAgentDbId]: loggedInAgentDbId ?? null,
      },
      { merge: true },
    );

    return deleteDoc(ref);
  }

  async updateFields(value, id: string, table: string, skipListUpdate = false): Promise<T> {
    const currentUser = this.auth?.currentUser;
    const idTokenResult = await this.auth.currentUser.getIdTokenResult();
    const loggedInAgentDbId = idTokenResult?.claims?.agentDbId;
    const actionName = !(currentUser?.uid || currentUser?.email || loggedInAgentDbId)
      ? ChangeSourceType.system
      : value?.[BaseModelKeys.actionName];
    console.log('%cUPDATE_FIELDS sourceType set as ', 'color: green', actionName);

    const ref = doc(this.db, table, id).withConverter({
      fromFirestore: null,
      toFirestore: (item: T): DocumentData => {
        const data = Object.assign(this.toFirestore ? this.toFirestore(item) : item, {
          [BaseModelKeys.actionId]: uuid.v4(),
          [BaseModelKeys.actionName]: actionName,
          [BaseModelKeys.updatedDate]: new Date(),
          [BaseModelKeys.updatedBy]: currentUser?.uid ?? null,
          [BaseModelKeys.updatedByEmail]: currentUser?.email ?? null,
          [BaseModelKeys.updatedByAgentDbId]: loggedInAgentDbId ?? null,
        });

        return data;
      },
    });

    if (skipListUpdate) {
      this.updatesToSkip.add(ref.id);
    }
    console.log('value', value);

    await setDoc(ref, value, { merge: true });

    return this.getById(table, id);
  }

  /**
   * REPLACES the document — this is a bare `setDoc`, so every field absent from `value`
   * is DELETED. `updateFields` above passes `{ merge: true }` and is what almost every
   * caller actually wants.
   *
   * The distinction stops being academic the moment a caller hands over anything less
   * than the whole document: a grid passing only its edited cells, or a form hydrated
   * from a projection that carries a subset of the agent's fields, will silently erase
   * the rest.
   *
   * @deprecated Use updateFields instead
   */
  public async update(value, id: string, table: string, action: ChangeSourceType | null = null): Promise<T> {
    const actionName = !this.auth?.currentUser?.uid ? ChangeSourceType.system : action;
    console.log('%cUPDATE sourceType set as ', 'color: green', actionName);

    const ref = doc(this.db, table, id).withConverter({
      fromFirestore: null,
      toFirestore: (item: T): DocumentData => {
        return Object.assign(item, {
          [BaseModelKeys.actionName]: actionName,
          [BaseModelKeys.updatedDate]: new Date(),
          [BaseModelKeys.updatedBy]: this.auth?.currentUser?.uid ?? null,
        });
      },
    });

    await setDoc(ref, value);

    return this.getById(table, id);
  }

  public async getAllByQValue(table: string, queries: QueryParam[], docsLimit = 0, sortField?: string): Promise<T[]> {
    const queryConstraints: QueryConstraint[] = queries.map(query => {
      if (query.operation === 'in') {
        // debugger;
      }
      return where(query.field, query.operation, query.value);
    });
    if (docsLimit) {
      queryConstraints.push(limit(docsLimit));
    }

    const ref = collection(this.db, table).withConverter({
      toFirestore: this.normalizeUpdateRequest,
      fromFirestore: this.convertResponse,
    });
    const documentQuery = query(ref, ...queryConstraints);
    const snap = await getDocs(documentQuery).catch(e => {
      debugger;
      return { docs: [] };
    });
    const docsData = snap.docs.map(item => (item.exists() ? item.data() : null));

    if (sortField) {
      docsData.sort((left, right) =>
        String(left[sortField]).localeCompare(String(right[sortField]), 'en', localeCompareOptions),
      );
    }

    return docsData;
  }

  convertResponse = (snapshot: QueryDocumentSnapshot, options: SnapshotOptions): any => {
    const isExist = snapshot.exists();
    if (!isExist) {
      return;
    }
    const data = snapshot.data(options);

    const normalizedData = Object.assign({}, data, {
      dbId: snapshot.id,
      [BaseModelKeys.createdDate]: this.dateFromTimestamp(data[BaseModelKeys.createdDate]),
      [BaseModelKeys.updatedDate]: this.dateFromTimestamp(data[BaseModelKeys.updatedDate]),
      [BaseModelKeys.deletedDate]: this.dateFromTimestamp(data[BaseModelKeys.deletedDate]),
    });

    return this.fromFirestore ? this.fromFirestore(normalizedData) : normalizedData;
  };

  private normalizeUpdateRequest = (item: T): DocumentData => {
    const data = Object.assign(this.toFirestore ? this.toFirestore(item) : item, {
      [BaseModelKeys.updatedDate]: new Date(),
      [BaseModelKeys.updatedBy]: this.auth?.currentUser?.uid ?? null,
    });

    return data;
  };

  private dateFromTimestamp = (item: Timestamp) => {
    if (!item) {
      return null;
    }

    if (isDate(item)) {
      return item;
    }

    let normalizedDate;

    if (item?.seconds) {
      normalizedDate = fromUnixTime(item.seconds);
    }

    return isValid(toDate(normalizedDate)) ? normalizedDate : null;
  };
}

export enum WhereFilterOperandKeys {
  less = '<',
  lessOrEqual = '<=',
  equal = '==',
  notEqual = '!=',
  more = '>',
  moreOrEqual = '>=',
  arrayContains = 'array-contains',
  in = 'in',
  arrayContainsAny = 'array-contains-any',
  notIn = 'not-in',
}

export class QueryParam {
  constructor(field: string | FieldPath, operation: WhereFilterOperandKeys, value: any) {
    this.field = field;
    this.operation = operation;
    this.value = value;
  }
  field: string | FieldPath;
  value: any;
  operation: WhereFilterOperandKeys;
}
